import {StepDataAnswer} from "components/chart/StepDataAnswer";
import * as moment from "moment";
import {e} from "mathjs";

interface ITrend {
  trend: string;
  slope: number;
}
interface ITrends {
  overall: ITrend;
  weekly: ITrend;
}
export class StepData {
  step;
  answers;
  trends:ITrends;
  trendDescription:string;

  constructor(step, feedItems) {
    this.step = step;
    this.answers = [];
    // extract the answers
    for (let i = 0; i < feedItems.length; i++) {
      if(!feedItems[i].completed) {
        continue;
      }

      if (feedItems[i].data) {
        for (let j = 0; j < feedItems[i].data.length; j++) {
          // only extract data for this step
          if (feedItems[i].data[j].stepId == step._id) {
            try {
              let answerValue = feedItems[i].data[j].answerValue;
              answerValue++;
              this.answers.push(new StepDataAnswer(answerValue, moment(feedItems[i].displayDate)));
            } catch(e) {
              console.log('answer must be an integer');
              console.log(e);
            }
            break;
          }
        }
      }
    }
    // sort the answers
    this.answers.sort(function (a, b) {
      return a.date.diff(b.date);
    });

    const metrics:ITrend[] = [];
    for(let i=0;i<this.answers.length;i++) {
      metrics.push(this.answers[i].asTrendObject());
    }

    this.trends = analyzeTrends(metrics);
    if(this.trends.overall.trend==="flat") {
      if(this.trends.weekly.trend==="improving"){
        this.trendDescription = `${step.shortName} over the past 7 days has been trending upwards.`;
      } else if(this.trends.weekly.trend==="declining"){
        this.trendDescription = `${step.shortName} over the past 7 days has been trending downwards, but is still relatively stable.`;
      } else {
        this.trendDescription = `${step.shortName} remains relatively unchanged.`;
      }
    } else if(this.trends.overall.trend==="improving") {
      if(this.trends.weekly.trend==="declining"){
        this.trendDescription = `${step.shortName} over the past 7 days has been trending downwards, but overall, has been trending upwards.`;
      } else if(this.trends.weekly.trend==="improving") {
        this.trendDescription = `${step.shortName} continues to trend upwards.`;
      } else if(this.trends.weekly.trend==="flat") {
        this.trendDescription = `${step.shortName} is remaining consistent, but overall, has been trending upwards.`;
      } else {
        this.trendDescription = `${step.shortName} is trending upwards.`;
      }
    } else if(this.trends.overall.trend==="declining") {
      if(this.trends.weekly.trend==="improving") {
        this.trendDescription = `${step.shortName} over the past 7 days has been trending upwards!`;
      } else if(this.trends.weekly.trend==="flat") {
        this.trendDescription = `${step.shortName} has leveled out this week, but overall, has been trending downwards.`;
      } else {
        this.trendDescription = `${step.shortName} is trending downwards.`;
      }
    } else {
      this.trendDescription = `More data is needed to calculate trend for ${step.shortName}.`;
    }

  }

  xyData() {
    const xy: Array<any> = [];
    for (let i = 0; i < this.answers.length; i++) {
      xy.push({x: this.answers[i].date, y: this.answers[i].answer});
    }
    return xy;
  }



}


/**
 * Determine trend from an array of { date, value } sorted by date.
 * Uses linear regression slope.
 */
function determineTrend(data, { threshold = 0 } = {}) {
  if (data.length < 2) {
    return { trend: "insufficient-data", slope: 0 };
  }

  // Sort by date
  // @ts-ignore
  const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Convert dates to numeric offsets (days since first date)
  const t0 = new Date(sorted[0].date).getTime();
  const xs = sorted.map(d => (new Date(d.date).getTime() - t0) / 86400000);
  const ys = sorted.map(d => d.value);

  // Linear regression slope
  const n = xs.length;
  const sumX = xs.reduce((a, b) => a + b, 0);
  const sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((sum, x, i) => sum + x * ys[i], 0);
  const sumX2 = xs.reduce((sum, x) => sum + x * x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

  let trend = "flat";
  if (slope > threshold) trend = "improving";
  else if (slope < -threshold) trend = "declining";

  return { trend, slope };
}


/**
 * Determine overall trend, and if enough data exists,
 * also determine the trend for the last 7 days.
 */
function analyzeTrends(data, { threshold = 0 } = {}) {
  if (!Array.isArray(data)) throw new Error("data must be an array");

  // Compute overall trend
  const overall = determineTrend(data, { threshold });

  // Filter last 7 days
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 86400000);

  const lastWeek = data.filter(d => new Date(d.date) >= weekAgo);

  let weekly = { trend: "insufficient-data", slope: 0 };
  if (lastWeek.length >= 2 && data.length >= 3) {
    weekly = determineTrend(lastWeek, { threshold });
  }

  return { overall, weekly };
}
